import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography, CircularProgress } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import api, { mediaUrl } from '../services/api.js';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import { AudioPlayer as NextAudioPlayer } from '../next/ui';

/**
 * AudioPlayer — מנגן הקלטה מהשרת
 * @param {string} [src] — endpoint אודיו לפי מזהה, למשל "/messages/<id>/audio".
 *   מנוגן ישירות ב-<audio> (עם ?t=) — ההשמעה מתחילה תוך כדי הורדה.
 * @param {string} [path] — נתיב יחסי בימות, למשל "77/7/2/000.wav" (דרך proxyUrl, כשאין src;
 *   נטען כולו ל-blob לפני ההשמעה)
 * @param {string} [label]
 * @param {string} [proxyUrl='/members/recording-proxy'] — נתיב יחסי תחת /api
 */
/** In the new design the crm-kit player (new-design look) plays the same src / path. */
export default function AudioPlayer(props) {
  const inside = useCkInside();
  return inside ? <NextAudioPlayer {...props} /> : <AudioPlayerLegacy {...props} />;
}

function AudioPlayerLegacy({
  src,
  path,
  label,
  proxyUrl = '/members/recording-proxy',
}) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState('');
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);

  const releaseAudio = () => {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
      audioRef.current = null;
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  // יציאה מהמסך / מעבר עמוד בטבלה — עוצרים את ההזרמה
  useEffect(() => releaseAudio, []);

  /** blob מלא דרך proxy (מצב path) */
  const loadBlobUrl = async () => {
    const { data, headers } = await api.get(proxyUrl, {
      params: { path },
      responseType: 'blob',
      // אל תשלח application/json על בקשת מדיה
      headers: { 'Content-Type': undefined },
    });
    const ct = String(headers['content-type'] || data?.type || '');
    if (ct.includes('application/json') || ct.includes('text/')) {
      const text = await data.text();
      console.error(`[AudioPlayer] ❌ לא-אודיו: ${ct} body:${text.slice(0, 200)}`);
      throw new Error('השרת החזיר שגיאה במקום אודיו');
    }
    const playBlob = ct.startsWith('audio/') ? data : new Blob([data], { type: 'audio/wav' });
    blobUrlRef.current = URL.createObjectURL(playBlob);
    return blobUrlRef.current;
  };

  const toggle = async () => {
    if (playing && audioRef.current) {
      // מצב src: משחררים — בהשמעה הבאה כתובת עם טוקן עדכני
      if (src) releaseAudio();
      else {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlaying(false);
      return;
    }
    setError('');
    setLoading(true);
    try {
      if (!audioRef.current) {
        const url = src ? await mediaUrl(src) : (blobUrlRef.current || await loadBlobUrl());
        const audio = new Audio();
        audio.preload = 'auto';
        audio.onended = () => setPlaying(false);
        audio.onerror = () => {
          console.error(`[AudioPlayer] ❌ שגיאת ניגון ${src || `path:"${path}"`}`, audio.error?.code);
          releaseAudio();
          setError('שגיאה בניגון');
          setPlaying(false);
          setLoading(false);
        };
        audio.src = url;
        audioRef.current = audio;
      }
      await audioRef.current.play();
      setPlaying(true);
    } catch (err) {
      console.error(`[AudioPlayer] ❌ ${src || `path:"${path}"`}`, err?.response?.status, err?.message);
      releaseAudio();
      const status = err?.response?.status;
      setError(status === 403 ? 'אין הרשאה' : 'שגיאה בטעינת הקלטה');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Tooltip title={label || (playing ? 'עצור הקלטה' : 'נגן הקלטה')}>
        <span>
          <IconButton
            size="small"
            onClick={toggle}
            disabled={loading || !(src || path)}
            color={playing ? 'error' : 'primary'}
          >
            {loading
              ? <CircularProgress size={16} />
              : playing
                ? <StopCircleOutlinedIcon fontSize="small" />
                : <PlayCircleOutlineIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      {error && (
        <Typography variant="caption" color="error">{error}</Typography>
      )}
    </Box>
  );
}
